
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import Header from "@/components/Header";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import TutorialHeader from "@/components/tutorial/TutorialHeader";
import TutorialContent from "@/components/tutorial/TutorialContent";
import TutorialSidebar from "@/components/tutorial/TutorialSidebar";
import TutorialNotFound from "@/components/tutorial/TutorialNotFound";
import { supabase } from "@/integrations/supabase/client";
import { Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TutorialDetails = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useRole();

  // Fetch tutorial by either slug or ID
  const { data: tutorial, isLoading, error } = useQuery({
    queryKey: ['tutorial', identifier],
    queryFn: async () => {
      if (!identifier) return null;

      // Try to fetch by slug first
      let { data, error } = await supabase
        .from('tutorials')
        .select(`*`)
        .eq('slug', identifier)
        .eq('is_edit_suggestion', false)
        .single();

      // If not found by slug, try by ID (for backward compatibility)
      if (error && error.code === 'PGRST116') {
        const result = await supabase
          .from('tutorials')
          .select(`*`)
          .eq('id', identifier)
          .eq('is_edit_suggestion', false)
          .single();
        
        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      return data;
    },
    enabled: !!identifier
  });

  const handleEdit = () => {
    if (tutorial) {
      navigate(`/tutorial-creator?edit=${tutorial.id}`);
    }
  };

  const handleDelete = async () => {
    if (!tutorial) return;

    try {
      const { error } = await supabase
        .from('tutorials')
        .delete()
        .eq('id', tutorial.id);

      if (error) throw error;

      toast.success("Tutorial deleted successfully");
      navigate('/tutorials');
    } catch (error) {
      console.error('Error deleting tutorial:', error);
      toast.error("Failed to delete tutorial");
    }
  };

  // Check if user can edit/delete this tutorial
  const canEdit = user && (isAdmin || user.id === tutorial?.created_by);
  const canDelete = user && (isAdmin || user.id === tutorial?.created_by);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="container py-8 pt-[64px]">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-3/4 mb-4"></div>
            <div className="h-64 bg-gray-300 rounded mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tutorial) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <TutorialNotFound />
      </div>
    );
  }

  // Safely access content.sections with proper type checking
  const getSteps = () => {
    if (!tutorial.content || typeof tutorial.content !== 'object') return [];
    
    const content = tutorial.content as any;
    if (content.sections && Array.isArray(content.sections) && content.sections.length > 0) {
      return content.sections[0].steps || [];
    }
    
    return [];
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <div className="container py-8 pt-[64px]">
        <TooltipProvider>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <TutorialHeader 
                    title={tutorial.title}
                    filters={tutorial.tags || []}
                    imageUrl={tutorial.cover_image}
                  />
                </div>
                
                {canEdit && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEdit}
                      className="flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Tutorial</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this tutorial? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </div>
              
              <TutorialContent 
                description={tutorial.description}
                fullDescription={tutorial.description}
                steps={getSteps()}
              />
            </div>
            
            <div className="md:col-span-1">
              <TutorialSidebar 
                category="tutorial"
                subcategory="guide"
                difficulty={tutorial.difficulty === 'Beginner' ? 1 : tutorial.difficulty === 'Intermediate' ? 2 : 3}
                requiresRoot={false}
                filters={tutorial.tags || []}
                videoLink={tutorial.video_guide}
                writtenGuide={tutorial.written_guide}
              />
            </div>
          </div>
        </TooltipProvider>
      </div>
      
      <footer className="bg-card border-t border-border py-6 mt-auto">
        <div className="container">
          <p className="text-sm text-center text-muted-foreground">
            3DP Hub | The ultimate directory for 3D printer parts and upgrades
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TutorialDetails;
